// AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (!token || !storedUser) {
          setLoading(false);
          return;
        }

        // Set initial user state from localStorage
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser({ ...parsedUser, token });
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          handleAuthFailure();
          return;
        }

        // Verify token with backend
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Token verification failed');
          }

          const userData = await response.json();
          const updatedUser = {
            ...userData,
            token
          };

          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } catch (error) {
          console.warn('Token verification failed:', error);
          // Don't automatically logout on verification failure
          // This allows offline functionality and retries
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        handleAuthFailure();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const handleAuthFailure = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      if (!response.token || !response.user) {
        throw new Error('Invalid response from auth service');
      }

      const userData = {
        id: response.user.userId || response.user.id,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        token: response.token
      };

      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      return userData;
    } catch (error) {
      console.error('Login error:', error);
      handleAuthFailure();
      throw error;
    }
  };

  const logout = () => {
    handleAuthFailure();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ 
      user,
      login,
      logout,
      isAuthenticated: !!user,
      token: user?.token
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};